import * as React from 'react';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
import { IStore } from 'redux/IStore';
import { reduxForm, getFormSyncErrors, Field } from 'redux-form';
import { Button, ErrorSummary, Checkout, WizzardSteps, WhiteButton, CartContainer } from 'components';
import { PortalPageSection, ButtonsSection, PageTitle } from 'components/PortalStructure';
import { InjectedRouter } from 'react-router';
import { ICartObject, ICart, ICartAction } from 'models/cart/cart';
import { getCountryDomainValues } from 'redux/modules/domainvalues';
import { IDomainValue } from 'models/common';
import { IDomainValuesAction } from 'models/domainvalues/domainvalues';
import { PortalSelectMenu, PortalInputField, PortalSwitchField } from 'components/FormControls';
import { maxLength, required } from 'helpers/validationattributes';
import { resetStripeSession } from 'redux/modules/cart';
import { preventRouterLeave } from 'helpers/hooks';
const { connect } = require('react-redux');
const config = require('../../../../../config/main');
require('./style.css');

declare var window: any;

interface IProps {
	currentFormValues: ICartObject;
	initialValues: ICartObject;
	cart?: ICart;
	location?: any;
	router: InjectedRouter;
	route: any;
	countryDomainValues: IDomainValue[];

	/* form props */
	pristine: boolean;
	dirty: boolean;
	submitting: boolean;
	handleSubmit: any;
	submitFailed: boolean;
	formErrors?: any;

	/* functions props */
	t?: (key: string) => string;
	previousPage?: () => void;
	getCountryDomainValues?: () => IDomainValuesAction;
	change: (field: string, value: any) => void;
	createNewTransaction?: (nonce: string, amount: number) => ICartAction;
	goToShoppingBagStep: () => ICartAction;
	resetStripeSession: () => ICartAction;
	onSubmit?: (form: ICartObject) => void;
	goToShop: (id: string, target?: string) => void;
	goToProduct: (id: string, target?: string) => void;
}

const maxLength25 = maxLength(25);
const maxLength50 = maxLength(50);
const maxLength255 = maxLength(255);

@preventRouterLeave()
@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class CheckoutPaymentForm extends React.Component<IProps, any> {
	private instance: any = null;

	constructor(props) {
		super(props);

		this.onSubmit = this.onSubmit.bind(this);
		this.handleBillingAddressSameAsDeliveryAddress = this.handleBillingAddressSameAsDeliveryAddress.bind(this);
	}

	public componentDidMount() {
		this.props.getCountryDomainValues();
		this.instance = window.Stripe(config.app.stripeSettings.publicKey);
	}

	public componentWillUnmount() {
		this.props.resetStripeSession();
		this.props.router.setRouteLeaveHook(this.props.route, null);
	}

	public async componentDidUpdate(prevProps: IProps) {
		if (prevProps.cart.paymentSessionId === null && this.props.cart.paymentSessionId !== null) {
			this.instance
				.redirectToCheckout({
					sessionId: this.props.cart.paymentSessionId,
				})
				.then((result) => {
					if (result.error) {
						console.error(result.error.message);
					}
				});
		}
	}

	public onSubmit(form: ICartObject) {
		form.order.currencyKey = this.props.cart.shoppingBasket.currencyKey;
		this.props.onSubmit(form);
	}

	private handleBillingAddressSameAsDeliveryAddress(e: any) {
		this.props.change('order.billingAddressSameAsDeliveryAddress', e.target.checked);
		if (!e.target.checked) {
			this.props.change('order.billingName', null);
			this.props.change('order.billingAddressLine', null);
			this.props.change('order.billingZIPCode', null);
			this.props.change('order.billingCity', null);
			this.props.change('order.billingState', null);
			this.props.change('order.billingCountryKey', null);
		} else {
			const { currentFormValues } = this.props;
			this.props.change('order.billingName', currentFormValues.order.shippingName);
			this.props.change('order.billingAddressLine', currentFormValues.order.shippingAddressLine);
			this.props.change('order.billingZIPCode', currentFormValues.order.shippingZIPCode);
			this.props.change('order.billingCity', currentFormValues.order.shippingCity);
			this.props.change('order.billingState', currentFormValues.order.shippingState);
			this.props.change('order.billingCountryKey', currentFormValues.order.shippingCountryKey);
		}
	}

	private mapErrorKeyToMessage(key: string, t: (key: string) => string) {
		switch (key) {
			case 'billingName':
				return t('Cart_CheckoutAddress_Label_NameAndSurname');

			case 'billingAddressLine':
				return t('Cart_CheckoutAddress_Label_StreetAndNumber');

			case 'billingZIPCode':
				return t('Cart_CheckoutAddress_Label_PostCode');

			case 'billingCity':
				return t('Cart_CheckoutAddress_Label_City');

			case 'billingCountryKey':
				return t('Cart_CheckoutAddress_Label_Country');

			default:
				return key;
		}
	}

	public render() {
		const { handleSubmit, formErrors, submitFailed, location, countryDomainValues, t, currentFormValues, cart } =
			this.props;
		// eslint-disable-next-line @typescript-eslint/no-this-alias
		const self = this;

		const errorSummaryProps = {
			formErrors,
			submitFailed,
			error: this.props.cart ? this.props.cart.error : false,
			message: this.props.cart ? this.props.cart.message : null,
			location,
			mapErrorKeyToMessage: this.mapErrorKeyToMessage,
			customFormErrorsParseFunc: () => {
				const errors = [];
				Object.keys(formErrors).forEach((key) => {
					Object.keys(formErrors[key]).forEach((innerKey) => {
						if (formErrors[key][innerKey]) {
							errors.push({ error: self.mapErrorKeyToMessage(innerKey, t) + ': ' + formErrors[key][innerKey] });
						}
					});
				});
				return errors;
			},
		};

		let countries = [];
		if (countryDomainValues) {
			countries = countryDomainValues.map((country: IDomainValue) => {
				return {
					key: country.key,
					value: t(`DV_Countries_${country.key}`),
				};
			});
			countries.sort(function (a: IDomainValue, b: IDomainValue) {
				if (a.value < b.value) {
					return -1;
				}
				if (a.value > b.value) {
					return 1;
				}
				return 0;
			});
		}

		return (
			<div className="cartCheckoutPayment">
				<PageTitle title={t('Cart_CheckoutAddress_Label_Checkout')} />
				<CartContainer>
					<div className="col-md-4">
						<Checkout
							items={cart.shoppingBasket.items}
							total={cart.shoppingBasket.total}
							goToShoppingBagStep={this.props.goToShoppingBagStep}
							itemsTotal={cart.shoppingBasket.itemsTotal}
							shippingPrice={cart.shoppingBasket.shippingPrice}
							currencyKey={cart.shoppingBasket.currencyKey}
							goToShop={this.props.goToShop}
							goToProduct={this.props.goToProduct}
						/>
					</div>
					<div className="col-md-8">
						<div className="paymentStep">
							<div className="profileError">
								<ErrorSummary {...errorSummaryProps} />
							</div>
							<form onSubmit={handleSubmit(this.onSubmit)}>
								<PortalPageSection title={t('Cart_CheckoutPayment_Label_Payment')}>
									<div className="row">
										<div className="col-md-12">
											<Field
												name="billingAddressSameAsDeliveryAddress"
												component={PortalSwitchField}
												type="checkbox"
												label={t('Cart_CheckoutPayment_Label_BillingAddressSameAsDeliveryAddress')}
												onChangeAction={this.handleBillingAddressSameAsDeliveryAddress}
											/>
										</div>
									</div>

									<div className="row">
										<div className="col-md-12">
											<Field
												name="order.billingName"
												component={PortalInputField}
												type="text"
												label={t('Cart_CheckoutAddress_Label_NameAndSurname')}
												validate={[maxLength50, required]}
												readOnly={currentFormValues.billingAddressSameAsDeliveryAddress}
											/>
										</div>
									</div>

									<div className="row">
										<div className="col-md-12">
											<Field
												name="order.billingAddressLine"
												component={PortalInputField}
												type="text"
												label={t('Cart_CheckoutAddress_Label_StreetAndNumber')}
												validate={[maxLength255, required]}
												readOnly={currentFormValues.billingAddressSameAsDeliveryAddress}
											/>
										</div>
									</div>

									<div className="row">
										<div className="col-md-2">
											<Field
												name="order.billingZIPCode"
												component={PortalInputField}
												type="text"
												label={t('Cart_CheckoutAddress_Label_PostCode')}
												validate={[maxLength25, required]}
												readOnly={currentFormValues.billingAddressSameAsDeliveryAddress}
											/>
										</div>
										<div className="col-md-5">
											<Field
												name="order.billingCity"
												component={PortalInputField}
												type="text"
												label={t('Cart_CheckoutAddress_Label_City')}
												validate={[maxLength255, required]}
												readOnly={currentFormValues.billingAddressSameAsDeliveryAddress}
											/>
										</div>
										<div className="col-md-5">
											<Field
												name="order.billingCountryKey"
												component={PortalSelectMenu}
												items={countries}
												isRequired={true}
												label={t('Cart_CheckoutAddress_Label_Country')}
												isDisabled={currentFormValues.billingAddressSameAsDeliveryAddress}
											/>
										</div>
									</div>
								</PortalPageSection>
								<ButtonsSection>
									<div className="row">
										<div className="col-sm-6">
											<div className="contentLeft floatLeft">
												<WizzardSteps currentStep={3} numberOfSteps={3} />
												<div className="clear" />
											</div>
											<div className="clear" />
										</div>
										<div className="col-sm-6">
											<div className="contentRight floatRight">
												<WhiteButton type="button" onClick={this.props.previousPage}>
													{t('BSPortal_Common_Back')}
												</WhiteButton>
												<Button type="submit" disabled={cart && cart.isFetching}>
													{t('Cart_CheckoutPayment_Button_SubmitOrder')}
												</Button>
												<div className="clear" />
											</div>
											<div className="clear" />
										</div>
									</div>
								</ButtonsSection>
								<PortalPageSection>
									<span className="termsConditionText">{t('Cart_CheckoutPayment_Label_TermsAndConditions')}</span>
								</PortalPageSection>
							</form>
						</div>
					</div>
				</CartContainer>
			</div>
		);
	}
}

let CheckoutPayment = reduxForm({
	form: c.FORM_NAME_CART,
	destroyOnUnmount: false,
	forceUnregisterOnUnmount: true,
})(CheckoutPaymentForm);

CheckoutPayment = connect(
	(state: IStore) => {
		let currentFormValues: ICartObject = null;
		if (state.form[c.FORM_NAME_CART] && state.form[c.FORM_NAME_CART].values) {
			currentFormValues = state.form[c.FORM_NAME_CART].values;
		} else {
			currentFormValues = state.cart.cart;
		}

		return {
			initialValues: state.cart.cart,
			cart: state.cart,
			currentFormValues,
			formErrors: getFormSyncErrors(c.FORM_NAME_CART)(state),
			countryDomainValues: state.domainValues.countries,
		};
	},
	(dispatch) => ({
		getCountryDomainValues: () => dispatch(getCountryDomainValues()),
		resetStripeSession: () => dispatch(resetStripeSession()),
	}),
)(CheckoutPayment);

export { CheckoutPayment };
